import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Plus, UserRound } from 'lucide-react';

import { StarlingMark } from '@/components/StarlingMark';
import { useLogout, useSession } from '@/auth/hooks';
import { SUPPORTED_LANGUAGES, changeLanguage, isLanguage, type Language } from '@/i18n';
import styles from './AppShell.module.css';

// Оболочка приложения (шапка + контент + футер) по прототипу партнёра.
// Отличия от прототипа, осознанные:
//   * переключатель языка — RU/KK; EN из прототипа не делаем (starling-vision.md §2);
//   * рамки телефона и сплэш-экрана нет — это артефакты презентации макета;
//   * рекламная карусель не рендерится: платное поднятие/реклама — V2 (§2);
//   * кнопка «+» (публикация) — FAB прототипа (V1.3-c): без сессии гард
//     /post сам уводит на /login с возвратом;
//   * в шапке добавлен вход/меню профиля (V1.3-b) — экрана в прототипе нет,
//     собран из его примитивов (пилюля в цветах шапки + выпадающее меню).

function LanguageSwitcher(): JSX.Element {
  const { t, i18n } = useTranslation();
  const active = i18n.resolvedLanguage ?? i18n.language;

  return (
    <div className={styles.langSwitch} role="group" aria-label={t('lang.label')}>
      {SUPPORTED_LANGUAGES.map((lang: Language) => {
        const isActive = isLanguage(active) && active === lang;
        return (
          <button
            key={lang}
            type="button"
            aria-pressed={isActive}
            className={`${styles.langButton} ${isActive ? styles.langButtonActive : ''}`}
            onClick={() => changeLanguage(lang)}
          >
            {t(`lang.${lang}`)}
          </button>
        );
      })}
    </div>
  );
}

function UserArea(): JSX.Element {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated) {
    return (
      <Link to="/login" className={styles.loginLink}>
        <UserRound size={13} aria-hidden="true" />
        {t('header.login')}
      </Link>
    );
  }

  return (
    <div className={styles.userMenu}>
      <button
        type="button"
        className={styles.userButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <UserRound size={13} aria-hidden="true" />
        <span className={styles.userName}>{user?.name || t('header.profile')}</span>
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div className={styles.menu} role="menu" aria-label={t('header.menu')}>
          <Link
            role="menuitem"
            to="/my/organization"
            className={styles.menuItem}
            onClick={() => setOpen(false)}
          >
            {t('header.myOrganization')}
          </Link>
          <Link
            role="menuitem"
            to="/my/listings"
            className={styles.menuItem}
            onClick={() => setOpen(false)}
          >
            {t('header.myListings')}
          </Link>
          <Link
            role="menuitem"
            to="/my/ads"
            className={styles.menuItem}
            onClick={() => setOpen(false)}
          >
            {t('header.myAds')}
          </Link>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              logout();
              navigate('/');
            }}
          >
            {t('header.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Плавающая кнопка «+» — публикация объявления (FAB прототипа: янтарный круг
 * справа внизу). Прячем на формах (публикация, правка, заявка на рекламу) и на входе; проверку
 * сессии не делаем — RequireAuth на /post уводит на /login с путём возврата.
 */
function PublishFab(): JSX.Element | null {
  const { t } = useTranslation();
  const location = useLocation();
  if (
    location.pathname === '/post' ||
    location.pathname === '/ads/new' ||
    location.pathname === '/login' ||
    /^\/my\/listings\/[^/]+\/edit$/.test(location.pathname)
  ) {
    return null;
  }
  return (
    <Link to="/post" className={styles.fab} aria-label={t('fab.publish')}>
      <Plus size={22} aria-hidden="true" />
    </Link>
  );
}

export function AppShell(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.brand}>
            <StarlingMark size={30} />
            <span className={styles.brandText}>
              <span className={styles.brandName}>{t('app.name')}</span>
              <span className={styles.brandTagline}>{t('app.tagline')}</span>
            </span>
          </Link>
          <div className={styles.headerActions}>
            <UserArea />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <PublishFab />

      <footer className={styles.footer}>
        <div className={styles.footerRule} />
        {/* Заглушки до V1.4: тексты оферты и контактов появятся к выкатке. */}
        <button type="button" className={styles.footerLink} aria-disabled="true">
          {t('footer.offer')}
        </button>
        <button type="button" className={styles.footerLink} aria-disabled="true">
          {t('footer.contacts')}
        </button>
      </footer>
    </div>
  );
}
